import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Plus, Trash2, Edit, Calendar, Store, DollarSign, Save, X,
  BarChart3, ArrowLeftRight, ChevronDown, RotateCcw, TrendingUp, TrendingDown, Minus
} from 'lucide-react';
import { Button } from '../components/ui/button';
import CashDoc from '../components/CashDoc';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../components/ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../components/ui/alert-dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import { useAuth } from '../contexts/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function fmtCurrency(v) {
  return new Intl.NumberFormat('es-ES', { style: 'currency', currency: 'EUR' }).format(v || 0);
}

const MONTHS = ['Enero','Febrero','Marzo','Abril','Mayo','Junio','Julio','Agosto','Septiembre','Octubre','Noviembre','Diciembre'];

export default function DailyCash() {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin' || user?.role === 'super_admin';

  const [records, setRecords] = useState([]);
  const [stores, setStores] = useState([]);
  const [summary, setSummary] = useState({ total_cash: 0, total_other: 0, total_combined: 0, record_count: 0 });
  const [loading, setLoading] = useState(true);

  // Filters (admin only)
  const [filterStore, setFilterStore] = useState('all');
  const [filterDay, setFilterDay] = useState('');
  const [filterMonth, setFilterMonth] = useState(String(new Date().getMonth() + 1));
  const [filterYear, setFilterYear] = useState(String(new Date().getFullYear()));

  // Dialog
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ date: '', store_id: '', cash_amount: '', other_amount: '' });
  const [saving, setSaving] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [docFiles, setDocFiles] = useState([]);

  // Store management dialog
  const [storeDialogOpen, setStoreDialogOpen] = useState(false);
  const [storeForm, setStoreForm] = useState({ name: '' });
  const [editingStore, setEditingStore] = useState(null);
  const [deleteStoreId, setDeleteStoreId] = useState(null);

  // Reset cash
  const [resetDialogOpen, setResetDialogOpen] = useState(false);
  const [resetting, setResetting] = useState(false);

  // Compare
  const [compareData, setCompareData] = useState(null);
  const [compareMode, setCompareMode] = useState('month'); // 'month' or 'range'
  const [cmpStore, setCmpStore] = useState('all');
  const [cmp1Month, setCmp1Month] = useState(String(new Date().getMonth() + 1));
  const [cmp1Year, setCmp1Year] = useState(String(new Date().getFullYear()));
  const [cmp2Month, setCmp2Month] = useState(String(new Date().getMonth() + 1));
  const [cmp2Year, setCmp2Year] = useState(String(new Date().getFullYear() - 1));
  const [cmp1Start, setCmp1Start] = useState('');
  const [cmp1End, setCmp1End] = useState('');
  const [cmp2Start, setCmp2Start] = useState('');
  const [cmp2End, setCmp2End] = useState('');

  const fetchStores = useCallback(async () => {
    try { setStores((await axios.get(`${API}/stores`)).data); } catch {}
  }, []);

  const fetchRecords = useCallback(async () => {
    try {
      const params = {};
      if (isAdmin) {
        if (filterStore !== 'all') params.store_id = filterStore;
        if (filterDay) {
          params.day = filterDay;
        } else {
          if (filterMonth) params.month = filterMonth;
          if (filterYear) params.year = filterYear;
        }
      }
      const [recRes, sumRes] = await Promise.all([
        axios.get(`${API}/daily-cash`, { params }),
        isAdmin ? axios.get(`${API}/daily-cash/summary`, { params }) : Promise.resolve({ data: {} })
      ]);
      setRecords(recRes.data);
      if (isAdmin) setSummary(sumRes.data);
    } catch {} finally { setLoading(false); }
  }, [isAdmin, filterStore, filterDay, filterMonth, filterYear]);

  useEffect(() => { fetchStores(); }, [fetchStores]);
  useEffect(() => { fetchRecords(); }, [fetchRecords]);

  const openNew = () => {
    setEditing(null);
    setDocFiles([]);
    setForm({ date: new Date().toISOString().split('T')[0], store_id: stores[0]?.id || '', cash_amount: '', other_amount: '' });
    setDialogOpen(true);
  };

  const openEdit = (r) => {
    setEditing(r);
    setDocFiles([]);
    setForm({ date: r.date, store_id: r.store_id, cash_amount: r.cash_amount, other_amount: r.other_amount });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!form.date) { toast.error('La fecha es obligatoria'); return; }
    if (!form.store_id) { toast.error('Selecciona una tienda'); return; }
    const cash = parseFloat(form.cash_amount || 0);
    const other = parseFloat(form.other_amount || 0);
    if (cash < 0 || other < 0) { toast.error('Los importes no pueden ser negativos'); return; }
    setSaving(true);
    try {
      const payload = { ...form, cash_amount: cash, other_amount: other };
      let recordId;
      if (editing) {
        await axios.put(`${API}/daily-cash/${editing.id}`, payload);
        recordId = editing.id;
        toast.success('Registro actualizado');
      } else {
        const res = await axios.post(`${API}/daily-cash`, payload);
        recordId = res.data?.id;
        toast.success('Registro creado');
      }
      if (docFiles.length && recordId) {
        try {
          const fd = new FormData();
          docFiles.forEach(f => fd.append('files', f));
          await axios.post(`${API}/daily-cash/${recordId}/documents`, fd);
          toast.success(docFiles.length === 1 ? 'Justificante adjuntado' : `${docFiles.length} justificantes adjuntados`);
        } catch (e) {
          toast.error(e.response?.data?.detail || 'El registro se guardó pero falló la subida de justificantes');
        }
      }
      setDocFiles([]);
      setDialogOpen(false); fetchRecords();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
    finally { setSaving(false); }
  };

  const handleDelete = async () => {
    try { await axios.delete(`${API}/daily-cash/${deleteId}`); toast.success('Registro eliminado'); setDeleteId(null); fetchRecords(); }
    catch { toast.error('Error al eliminar'); }
  };

  // Store CRUD
  const openNewStore = () => { setEditingStore(null); setStoreForm({ name: '' }); setStoreDialogOpen(true); };
  const openEditStore = (s) => { setEditingStore(s); setStoreForm({ name: s.name }); setStoreDialogOpen(true); };

  const handleSaveStore = async () => {
    if (!storeForm.name.trim()) { toast.error('El nombre es obligatorio'); return; }
    try {
      if (editingStore) {
        await axios.put(`${API}/stores/${editingStore.id}`, storeForm);
        toast.success('Tienda actualizada');
      } else {
        await axios.post(`${API}/stores`, storeForm);
        toast.success('Tienda creada');
      }
      setStoreDialogOpen(false); fetchStores();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
  };

  const handleDeleteStore = async () => {
    try { await axios.delete(`${API}/stores/${deleteStoreId}`); toast.success('Tienda eliminada'); setDeleteStoreId(null); fetchStores(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Error al eliminar'); }
  };

  // Reset / Cleanup
  const handleResetCash = async () => {
    setResetting(true);
    try {
      const res = await axios.delete(`${API}/daily-cash/cleanup`);
      const count = res.data.deleted_count || 0;
      // Reset all local form state
      setDialogOpen(false);
      setEditing(null);
      setForm({ date: '', store_id: '', cash_amount: '', other_amount: '' });
      setSaving(false);
      setDeleteId(null);
      if (count > 0) {
        toast.success(`${count} registro(s) incompleto(s) eliminado(s)`);
      } else {
        toast.success('Caja reiniciada correctamente');
      }
      fetchRecords();
    } catch {
      toast.error('Error al reiniciar la caja');
    } finally {
      setResetting(false);
      setResetDialogOpen(false);
    }
  };

  // Compare
  const handleCompare = async () => {
    try {
      const params = {};
      if (cmpStore !== 'all') params.store_id = cmpStore;
      if (compareMode === 'month') {
        params.month1 = cmp1Month; params.year1 = cmp1Year;
        params.month2 = cmp2Month; params.year2 = cmp2Year;
      } else {
        if (!cmp1Start || !cmp1End || !cmp2Start || !cmp2End) {
          toast.error('Selecciona fechas de inicio y fin para ambos periodos');
          return;
        }
        params.start_date1 = cmp1Start; params.end_date1 = cmp1End;
        params.start_date2 = cmp2Start; params.end_date2 = cmp2End;
      }
      const res = await axios.get(`${API}/daily-cash/compare`, { params });
      setCompareData(res.data);
    } catch { toast.error('Error al comparar'); }
  };

  const years = [];
  for (let y = 2020; y <= new Date().getFullYear() + 1; y++) years.push(y);

  return (
    <div className="p-4 md:p-6 space-y-6" data-testid="daily-cash-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <h1 className="text-2xl font-bold text-slate-800 font-['Barlow_Condensed']" data-testid="page-title">Caja Diaria</h1>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={() => setResetDialogOpen(true)} className="text-amber-600 border-amber-300 hover:bg-amber-50" data-testid="reset-cash-btn">
            <RotateCcw size={14} className="mr-1" /> Reiniciar Caja
          </Button>
          {isAdmin && (
            <Button variant="outline" size="sm" onClick={openNewStore} data-testid="manage-stores-btn">
              <Store size={14} className="mr-1" /> Tiendas
            </Button>
          )}
          <Button onClick={openNew} className="bg-violet-600 hover:bg-violet-700" data-testid="new-record-btn">
            <Plus size={16} className="mr-1" /> Nuevo Registro
          </Button>
        </div>
      </div>

      <Tabs defaultValue="records">
        <TabsList>
          <TabsTrigger value="records" data-testid="tab-records">Registros</TabsTrigger>
          {isAdmin && <TabsTrigger value="compare" data-testid="tab-compare">Comparativa</TabsTrigger>}
          {isAdmin && <TabsTrigger value="stores" data-testid="tab-stores">Tiendas</TabsTrigger>}
        </TabsList>

        {/* ====== RECORDS TAB ====== */}
        <TabsContent value="records" className="space-y-4 mt-4">
          {/* Filters - admin only */}
          {isAdmin && (
            <Card>
              <CardContent className="p-4">
                <div className="grid grid-cols-2 md:grid-cols-5 gap-3 items-end">
                  <div>
                    <Label className="text-xs text-slate-500">Tienda</Label>
                    <Select value={filterStore} onValueChange={setFilterStore}>
                      <SelectTrigger data-testid="filter-store"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">Todas</SelectItem>
                        {stores.map(s => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs text-slate-500">Día exacto</Label>
                    <Input
                      type="date"
                      value={filterDay}
                      onChange={e => setFilterDay(e.target.value)}
                      className={filterDay ? 'border-violet-400 ring-1 ring-violet-200' : ''}
                      data-testid="filter-day"
                    />
                  </div>
                  <div>
                    <Label className={`text-xs ${filterDay ? 'text-slate-300' : 'text-slate-500'}`}>Mes</Label>
                    <Select value={filterMonth} onValueChange={setFilterMonth} disabled={!!filterDay}>
                      <SelectTrigger data-testid="filter-month" className={filterDay ? 'opacity-50' : ''}><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {MONTHS.map((m, i) => <SelectItem key={i} value={String(i + 1)}>{m}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className={`text-xs ${filterDay ? 'text-slate-300' : 'text-slate-500'}`}>Año</Label>
                    <Select value={filterYear} onValueChange={setFilterYear} disabled={!!filterDay}>
                      <SelectTrigger data-testid="filter-year" className={filterDay ? 'opacity-50' : ''}><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {years.map(y => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <Button variant="outline" size="sm" onClick={() => { setFilterStore('all'); setFilterDay(''); setFilterMonth(String(new Date().getMonth() + 1)); setFilterYear(String(new Date().getFullYear())); }} data-testid="clear-filters-btn">
                    <X size={14} className="mr-1" /> Limpiar
                  </Button>
                </div>
                {filterDay && (
                  <p className="text-xs text-violet-600 mt-2" data-testid="day-filter-hint">
                    Filtrando por día: {new Date(filterDay + 'T00:00:00').toLocaleDateString('es-ES', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
                  </p>
                )}
              </CardContent>
            </Card>
          )}

          {/* Summary - admin only */}
          {isAdmin && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <Card><CardContent className="p-4 text-center"><p className="text-xs text-slate-500 uppercase">Registros</p><p className="text-2xl font-bold text-slate-800 font-['Barlow_Condensed']" data-testid="summary-count">{summary.record_count}</p></CardContent></Card>
              <Card><CardContent className="p-4 text-center"><p className="text-xs text-slate-500 uppercase">Total Caja</p><p className="text-2xl font-bold text-slate-800 font-['Barlow_Condensed']" data-testid="summary-cash">{fmtCurrency(summary.total_cash)}</p></CardContent></Card>
              <Card><CardContent className="p-4 text-center"><p className="text-xs text-slate-500 uppercase">Total Otros</p><p className="text-2xl font-bold text-slate-800 font-['Barlow_Condensed']" data-testid="summary-other">{fmtCurrency(summary.total_other)}</p></CardContent></Card>
              <Card className="bg-violet-50 border-violet-200"><CardContent className="p-4 text-center"><p className="text-xs text-violet-600 uppercase">Total Combinado</p><p className="text-2xl font-bold text-violet-700 font-['Barlow_Condensed']" data-testid="summary-combined">{fmtCurrency(summary.total_combined)}</p></CardContent></Card>
            </div>
          )}

          {/* Records table */}
          {loading ? (
            <div className="flex justify-center py-12"><div className="spinner w-8 h-8" /></div>
          ) : records.length === 0 ? (
            <Card><CardContent className="py-12 text-center"><DollarSign size={40} className="mx-auto mb-3 text-slate-300" /><p className="text-slate-400">No hay registros</p></CardContent></Card>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="records-table">
                <thead>
                  <tr className="border-b border-slate-200 bg-slate-50">
                    <th className="text-left p-3 font-medium text-slate-600">Fecha</th>
                    <th className="text-left p-3 font-medium text-slate-600">Tienda</th>
                    <th className="text-right p-3 font-medium text-slate-600">Caja</th>
                    <th className="text-right p-3 font-medium text-slate-600">Otros</th>
                    <th className="text-right p-3 font-medium text-slate-600">Total</th>
                    {isAdmin && <th className="text-left p-3 font-medium text-slate-600">Creado por</th>}
                    <th className="text-center p-3 font-medium text-slate-600">Justif.</th>
                    <th className="text-right p-3 font-medium text-slate-600">Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {records.map(r => (
                    <tr key={r.id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`record-${r.id}`}>
                      <td className="p-3">
                        <div>{r.date}</div>
                        {r.created_at && (
                          <span className="text-xs text-slate-400">{new Date(r.created_at).toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' })}</span>
                        )}
                      </td>
                      <td className="p-3 font-medium">{r.store_name}</td>
                      <td className="p-3 text-right">{fmtCurrency(r.cash_amount)}</td>
                      <td className="p-3 text-right">{fmtCurrency(r.other_amount)}</td>
                      <td className="p-3 text-right font-semibold text-violet-700">{fmtCurrency(r.total)}</td>
                      {isAdmin && <td className="p-3 text-slate-500 text-xs">{r.created_by_name}</td>}
                      <td className="p-3">
                        <CashDoc record={r} onUpdated={fetchRecords} />
                      </td>
                      <td className="p-3 text-right">
                        <div className="flex gap-1 justify-end">
                          <Button size="sm" variant="ghost" onClick={() => openEdit(r)} data-testid="edit-record-btn"><Edit size={14} /></Button>
                          {isAdmin && <Button size="sm" variant="ghost" className="text-red-500" onClick={() => setDeleteId(r.id)} data-testid="delete-record-btn"><Trash2 size={14} /></Button>}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </TabsContent>

        {/* ====== COMPARE TAB ====== */}
        {isAdmin && (
          <TabsContent value="compare" className="space-y-4 mt-4">
            {/* Filters Card */}
            <Card>
              <CardContent className="p-4 space-y-4">
                {/* Mode toggle + Store filter */}
                <div className="flex flex-wrap items-end gap-3">
                  <div>
                    <Label className="text-xs text-slate-500">Modo</Label>
                    <Select value={compareMode} onValueChange={(v) => { setCompareMode(v); setCompareData(null); }}>
                      <SelectTrigger className="w-36" data-testid="compare-mode"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="month">Por Mes</SelectItem>
                        <SelectItem value="range">Rango Fechas</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs text-slate-500">Tienda</Label>
                    <Select value={cmpStore} onValueChange={setCmpStore}>
                      <SelectTrigger className="w-48" data-testid="compare-store"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">Todas las tiendas</SelectItem>
                        {stores.map(s => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {/* Period selectors */}
                {compareMode === 'month' ? (
                  <div className="flex flex-wrap items-end gap-3">
                    <div>
                      <Label className="text-xs text-slate-500">Periodo 1</Label>
                      <div className="flex gap-2">
                        <Select value={cmp1Month} onValueChange={setCmp1Month}>
                          <SelectTrigger className="w-32" data-testid="cmp1-month"><SelectValue /></SelectTrigger>
                          <SelectContent>{MONTHS.map((m, i) => <SelectItem key={i} value={String(i + 1)}>{m}</SelectItem>)}</SelectContent>
                        </Select>
                        <Select value={cmp1Year} onValueChange={setCmp1Year}>
                          <SelectTrigger className="w-24" data-testid="cmp1-year"><SelectValue /></SelectTrigger>
                          <SelectContent>{years.map(y => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}</SelectContent>
                        </Select>
                      </div>
                    </div>
                    <ArrowLeftRight size={20} className="text-slate-400 mb-2" />
                    <div>
                      <Label className="text-xs text-slate-500">Periodo 2</Label>
                      <div className="flex gap-2">
                        <Select value={cmp2Month} onValueChange={setCmp2Month}>
                          <SelectTrigger className="w-32" data-testid="cmp2-month"><SelectValue /></SelectTrigger>
                          <SelectContent>{MONTHS.map((m, i) => <SelectItem key={i} value={String(i + 1)}>{m}</SelectItem>)}</SelectContent>
                        </Select>
                        <Select value={cmp2Year} onValueChange={setCmp2Year}>
                          <SelectTrigger className="w-24" data-testid="cmp2-year"><SelectValue /></SelectTrigger>
                          <SelectContent>{years.map(y => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}</SelectContent>
                        </Select>
                      </div>
                    </div>
                    <Button onClick={handleCompare} className="bg-violet-600 hover:bg-violet-700" data-testid="compare-btn">
                      <BarChart3 size={14} className="mr-1" /> Comparar
                    </Button>
                  </div>
                ) : (
                  <div className="flex flex-wrap items-end gap-3">
                    <div>
                      <Label className="text-xs text-slate-500">Periodo 1</Label>
                      <div className="flex gap-2">
                        <Input type="date" value={cmp1Start} onChange={e => setCmp1Start(e.target.value)} className="w-36 h-9" data-testid="cmp1-start" />
                        <span className="text-slate-400 self-center text-xs">a</span>
                        <Input type="date" value={cmp1End} onChange={e => setCmp1End(e.target.value)} className="w-36 h-9" data-testid="cmp1-end" />
                      </div>
                    </div>
                    <ArrowLeftRight size={20} className="text-slate-400 mb-2" />
                    <div>
                      <Label className="text-xs text-slate-500">Periodo 2</Label>
                      <div className="flex gap-2">
                        <Input type="date" value={cmp2Start} onChange={e => setCmp2Start(e.target.value)} className="w-36 h-9" data-testid="cmp2-start" />
                        <span className="text-slate-400 self-center text-xs">a</span>
                        <Input type="date" value={cmp2End} onChange={e => setCmp2End(e.target.value)} className="w-36 h-9" data-testid="cmp2-end" />
                      </div>
                    </div>
                    <Button onClick={handleCompare} className="bg-violet-600 hover:bg-violet-700" data-testid="compare-btn">
                      <BarChart3 size={14} className="mr-1" /> Comparar
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Results */}
            {compareData && (() => {
              const p1 = compareData.period1;
              const p2 = compareData.period2;
              const p1Label = p1.month ? `${MONTHS[p1.month - 1]} ${p1.year}` : `${p1.start_date} — ${p1.end_date}`;
              const p2Label = p2.month ? `${MONTHS[p2.month - 1]} ${p2.year}` : `${p2.start_date} — ${p2.end_date}`;

              // Merge all stores from both periods
              const allStores = new Set([...p1.by_store.map(s => s.store), ...p2.by_store.map(s => s.store)]);
              const storeRows = [...allStores].sort().map(store => {
                const s1 = p1.by_store.find(s => s.store === store) || { total_cash: 0, total_other: 0, total_combined: 0, record_count: 0 };
                const s2 = p2.by_store.find(s => s.store === store) || { total_cash: 0, total_other: 0, total_combined: 0, record_count: 0 };
                const diff = s2.total_combined - s1.total_combined;
                const pct = s1.total_combined > 0 ? (diff / s1.total_combined * 100) : (s2.total_combined > 0 ? 100 : 0);
                return { store, s1, s2, diff, pct };
              });

              const totalDiff = p2.totals.total_combined - p1.totals.total_combined;
              const totalPct = p1.totals.total_combined > 0 ? (totalDiff / p1.totals.total_combined * 100) : 0;

              return (
                <div className="space-y-4">
                  {/* Period summary cards */}
                  <div className="grid md:grid-cols-2 gap-4">
                    {[{ p: p1, label: p1Label, color: 'violet' }, { p: p2, label: p2Label, color: 'indigo' }].map(({ p, label, color }, idx) => (
                      <Card key={idx} className={`border-${color}-200`}>
                        <CardHeader className="pb-2">
                          <CardTitle className="text-lg font-['Barlow_Condensed']">{label}</CardTitle>
                        </CardHeader>
                        <CardContent>
                          <div className="grid grid-cols-3 gap-2 text-center">
                            <div className="bg-slate-50 rounded-lg p-3">
                              <p className="text-xs text-slate-500">Caja</p>
                              <p className="text-lg font-bold font-['Barlow_Condensed']">{fmtCurrency(p.totals.total_cash)}</p>
                            </div>
                            <div className="bg-slate-50 rounded-lg p-3">
                              <p className="text-xs text-slate-500">Otros</p>
                              <p className="text-lg font-bold font-['Barlow_Condensed']">{fmtCurrency(p.totals.total_other)}</p>
                            </div>
                            <div className={`bg-${color}-50 rounded-lg p-3`}>
                              <p className="text-xs text-slate-500">Total</p>
                              <p className="text-lg font-bold font-['Barlow_Condensed']">{fmtCurrency(p.totals.total_combined)}</p>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>

                  {/* Global difference */}
                  {(p1.totals.total_combined > 0 || p2.totals.total_combined > 0) && (
                    <Card className="bg-slate-50">
                      <CardContent className="p-4">
                        <div className="grid grid-cols-3 gap-4 text-center">
                          {[
                            { key: 'total_cash', label: 'Dif. Caja' },
                            { key: 'total_other', label: 'Dif. Otros' },
                            { key: 'total_combined', label: 'Dif. Total' }
                          ].map(({ key, label }) => {
                            const diff = p2.totals[key] - p1.totals[key];
                            const pct = p1.totals[key] > 0 ? (diff / p1.totals[key] * 100) : 0;
                            return (
                              <div key={key}>
                                <p className="text-xs text-slate-500 uppercase">{label}</p>
                                <p className={`text-xl font-bold font-['Barlow_Condensed'] flex items-center justify-center gap-1 ${diff > 0 ? 'text-green-600' : diff < 0 ? 'text-red-600' : 'text-slate-500'}`}>
                                  {diff > 0 ? <TrendingUp size={16} /> : diff < 0 ? <TrendingDown size={16} /> : <Minus size={16} />}
                                  {diff >= 0 ? '+' : ''}{fmtCurrency(diff)}
                                </p>
                                {pct !== 0 && <p className={`text-xs ${diff >= 0 ? 'text-green-500' : 'text-red-500'}`}>{diff >= 0 ? '+' : ''}{pct.toFixed(1)}%</p>}
                              </div>
                            );
                          })}
                        </div>
                      </CardContent>
                    </Card>
                  )}

                  {/* Per-store comparison table */}
                  {storeRows.length > 0 && (
                    <Card>
                      <CardHeader className="pb-2">
                        <CardTitle className="text-base font-['Barlow_Condensed']">Comparativa por Tienda</CardTitle>
                      </CardHeader>
                      <CardContent className="p-0">
                        <div className="overflow-x-auto">
                          <table className="w-full text-sm">
                            <thead>
                              <tr className="bg-slate-50 border-b border-slate-200">
                                <th className="text-left p-3 font-medium text-slate-600">Tienda</th>
                                <th className="text-right p-3 font-medium text-violet-600">{p1Label}</th>
                                <th className="text-right p-3 font-medium text-indigo-600">{p2Label}</th>
                                <th className="text-right p-3 font-medium text-slate-600">Diferencia</th>
                                <th className="text-right p-3 font-medium text-slate-600">%</th>
                              </tr>
                            </thead>
                            <tbody>
                              {storeRows.map(({ store, s1, s2, diff, pct }) => (
                                <tr key={store} className="border-b border-slate-100 hover:bg-slate-50">
                                  <td className="p-3 font-medium">{store}</td>
                                  <td className="p-3 text-right">{fmtCurrency(s1.total_combined)}</td>
                                  <td className="p-3 text-right">{fmtCurrency(s2.total_combined)}</td>
                                  <td className={`p-3 text-right font-semibold ${diff > 0 ? 'text-green-600' : diff < 0 ? 'text-red-600' : 'text-slate-400'}`}>
                                    {diff >= 0 ? '+' : ''}{fmtCurrency(diff)}
                                  </td>
                                  <td className={`p-3 text-right text-xs ${diff > 0 ? 'text-green-500' : diff < 0 ? 'text-red-500' : 'text-slate-400'}`}>
                                    {pct !== 0 ? `${pct >= 0 ? '+' : ''}${pct.toFixed(1)}%` : '—'}
                                  </td>
                                </tr>
                              ))}
                              {/* Totals row */}
                              <tr className="bg-slate-100 font-semibold">
                                <td className="p-3">TOTAL</td>
                                <td className="p-3 text-right">{fmtCurrency(p1.totals.total_combined)}</td>
                                <td className="p-3 text-right">{fmtCurrency(p2.totals.total_combined)}</td>
                                <td className={`p-3 text-right ${totalDiff > 0 ? 'text-green-600' : totalDiff < 0 ? 'text-red-600' : ''}`}>
                                  {totalDiff >= 0 ? '+' : ''}{fmtCurrency(totalDiff)}
                                </td>
                                <td className={`p-3 text-right text-xs ${totalDiff > 0 ? 'text-green-500' : totalDiff < 0 ? 'text-red-500' : ''}`}>
                                  {totalPct !== 0 ? `${totalPct >= 0 ? '+' : ''}${totalPct.toFixed(1)}%` : '—'}
                                </td>
                              </tr>
                            </tbody>
                          </table>
                        </div>
                      </CardContent>
                    </Card>
                  )}
                </div>
              );
            })()}
          </TabsContent>
        )}

        {/* ====== STORES TAB ====== */}
        {isAdmin && (
          <TabsContent value="stores" className="space-y-4 mt-4">
            <div className="flex justify-between items-center">
              <p className="text-sm text-slate-500">{stores.length} tiendas</p>
              <Button onClick={openNewStore} className="bg-violet-600 hover:bg-violet-700" data-testid="new-store-btn">
                <Plus size={16} className="mr-1" /> Nueva Tienda
              </Button>
            </div>
            {stores.length === 0 ? (
              <Card><CardContent className="py-12 text-center"><Store size={40} className="mx-auto mb-3 text-slate-300" /><p className="text-slate-400">No hay tiendas</p></CardContent></Card>
            ) : (
              <div className="grid gap-3">
                {stores.map(s => (
                  <Card key={s.id} className="hover:shadow-md transition-shadow" data-testid={`store-${s.id}`}>
                    <CardContent className="p-4 flex items-center gap-4">
                      <div className="w-10 h-10 bg-violet-100 rounded-lg flex items-center justify-center shrink-0">
                        <Store size={20} className="text-violet-600" />
                      </div>
                      <div className="flex-1"><p className="font-semibold text-slate-800" data-testid="store-name">{s.name}</p></div>
                      <div className="flex gap-2 shrink-0">
                        <Button size="sm" variant="outline" onClick={() => openEditStore(s)} data-testid="edit-store-btn"><Edit size={14} /></Button>
                        <Button size="sm" variant="outline" className="text-red-500 hover:bg-red-50" onClick={() => setDeleteStoreId(s.id)} data-testid="delete-store-btn"><Trash2 size={14} /></Button>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>
        )}
      </Tabs>

      {/* Record Dialog */}
      <Dialog open={dialogOpen} onOpenChange={(open) => {
        setDialogOpen(open);
        if (!open) {
          setEditing(null);
          setDocFiles([]);
          setForm({ date: '', store_id: '', cash_amount: '', other_amount: '' });
          setSaving(false);
        }
      }}>
        <DialogContent className="max-w-sm" data-testid="record-dialog">
          <DialogHeader><DialogTitle className="font-['Barlow_Condensed']">{editing ? 'Editar Registro' : 'Nuevo Registro'}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div><Label>Fecha</Label><Input type="date" value={form.date} onChange={e => setForm({ ...form, date: e.target.value })} data-testid="record-date-input" /></div>
            <div>
              <Label>Tienda</Label>
              <Select value={form.store_id} onValueChange={v => setForm({ ...form, store_id: v })}>
                <SelectTrigger data-testid="record-store-select"><SelectValue placeholder="Seleccionar tienda..." /></SelectTrigger>
                <SelectContent>{stores.map(s => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>Importe Caja</Label>
              <Input type="number" min="0" step="0.01" value={form.cash_amount} onChange={e => setForm({ ...form, cash_amount: e.target.value })} placeholder="0.00" data-testid="record-cash-input" />
            </div>
            <div>
              <Label>Importe Otros</Label>
              <Input type="number" min="0" step="0.01" value={form.other_amount} onChange={e => setForm({ ...form, other_amount: e.target.value })} placeholder="0.00" data-testid="record-other-input" />
            </div>
            <div>
              <Label>Justificantes (PDF/JPG/PNG, varios permitidos)</Label>
              <Input type="file" multiple accept=".pdf,.jpg,.jpeg,.png"
                onChange={e => setDocFiles(Array.from(e.target.files || []))}
                className="cursor-pointer file:text-violet-700"
                data-testid="record-doc-input" />
              {docFiles.length > 0 && (
                <p className="text-xs text-violet-700 mt-1" data-testid="record-doc-selected">{docFiles.length} archivo(s) seleccionado(s) para añadir</p>
              )}
              {editing?.receipt_docs?.length > 0 && (
                <p className="text-xs text-slate-500 mt-1 truncate" data-testid="record-doc-current">
                  Ya adjuntos: {editing.receipt_docs.length} · gestiónalos desde la columna "Justif." de la tabla
                </p>
              )}
            </div>
            <div className="bg-violet-50 rounded-lg p-3 flex items-center justify-between">
              <span className="text-sm text-violet-700">Total</span>
              <span className="text-lg font-bold text-violet-800 font-['Barlow_Condensed']" data-testid="record-total-preview">
                {fmtCurrency((parseFloat(form.cash_amount) || 0) + (parseFloat(form.other_amount) || 0))}
              </span>
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancelar</Button>
            <Button onClick={handleSave} disabled={saving} className="bg-violet-600 hover:bg-violet-700" data-testid="save-record-btn">
              {saving ? 'Guardando...' : <><Save size={14} className="mr-1" /> Guardar</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Store Dialog */}
      <Dialog open={storeDialogOpen} onOpenChange={setStoreDialogOpen}>
        <DialogContent className="max-w-xs" data-testid="store-dialog">
          <DialogHeader><DialogTitle className="font-['Barlow_Condensed']">{editingStore ? 'Editar Tienda' : 'Nueva Tienda'}</DialogTitle></DialogHeader>
          <div><Label>Nombre</Label><Input value={storeForm.name} onChange={e => setStoreForm({ name: e.target.value })} placeholder="Nombre de la tienda" data-testid="store-name-input" /></div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setStoreDialogOpen(false)}>Cancelar</Button>
            <Button onClick={handleSaveStore} className="bg-violet-600 hover:bg-violet-700" data-testid="save-store-btn">Guardar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete record confirm */}
      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent><AlertDialogHeader><AlertDialogTitle>Eliminar registro</AlertDialogTitle><AlertDialogDescription>Esta acción no se puede deshacer.</AlertDialogDescription></AlertDialogHeader>
          <AlertDialogFooter><AlertDialogCancel>Cancelar</AlertDialogCancel><AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700" data-testid="confirm-delete-record">Eliminar</AlertDialogAction></AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete store confirm */}
      <AlertDialog open={!!deleteStoreId} onOpenChange={() => setDeleteStoreId(null)}>
        <AlertDialogContent><AlertDialogHeader><AlertDialogTitle>Eliminar tienda</AlertDialogTitle><AlertDialogDescription>Esta acción no se puede deshacer.</AlertDialogDescription></AlertDialogHeader>
          <AlertDialogFooter><AlertDialogCancel>Cancelar</AlertDialogCancel><AlertDialogAction onClick={handleDeleteStore} className="bg-red-600 hover:bg-red-700" data-testid="confirm-delete-store">Eliminar</AlertDialogAction></AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Reset cash confirm */}
      <AlertDialog open={resetDialogOpen} onOpenChange={setResetDialogOpen}>
        <AlertDialogContent data-testid="reset-cash-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Reiniciar Caja</AlertDialogTitle>
            <AlertDialogDescription>
              Se eliminarán los registros incompletos (sin tienda, sin fecha, o con importes en 0) y se reiniciará el formulario. ¿Continuar?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={resetting}>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleResetCash} disabled={resetting} className="bg-amber-600 hover:bg-amber-700" data-testid="confirm-reset-cash">
              {resetting ? 'Reiniciando...' : 'Reiniciar'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
